import {
    backwardListFromIndex,
    forwardListFromIndex,
} from "../../../core/components/accordion/accordion";
import {
    componentClasses,
    componentName,
    defaultProps,
    eventMap,
    schema,
    views,
} from "../../../core/components/dropdown/dropdown";
import { icons } from "../../../core/components/tag/tag";
import { check, chevronDown } from "../../../core/js/helpers/built-in-icons";
import validateInitialization from "../../../core/js/helpers/validate-initialization";
import { getPosition } from "../../../core/js/positioning";
import {
    actions,
    createStore,
    dispatchEvent,
    reducers,
    renderState,
} from "../../../core/js/state-machine";
import {
    createCSSClass,
    createElement,
    debounce,
    isIE,
    isMobile,
    prefix,
    randomNumGenerator,
} from "../../../core/js/utilities";

import { DropdownItemTemplate, DropdownSeparatorTemplate } from "./dropdown.template";

const validateHTML = (element) => {
    const inputFieldElement = element.querySelector(`.${componentClasses.inputField}`);
    const inputWrapperElement = element.querySelector(`.${componentClasses.inputWrapper}`);
    const popupElement = element.querySelector(`.${componentClasses.popup.base}`);
    const listElement = element.querySelector(`.${componentClasses.list.base}`);
    const items = element.querySelectorAll(`.${componentClasses.itemOption}`);

    let itemsDataSelectedAttr = true;
    Array.from(items).forEach((item) => {
        if (!item.hasAttribute("data-selected")) itemsDataSelectedAttr = false;
    });

    if (!itemsDataSelectedAttr)
        console.warn(
            `One or more .${componentClasses.itemOption} doesn't have the data-selected attribute`
        );

    return inputFieldElement &&
        inputWrapperElement &&
        popupElement &&
        listElement &&
        itemsDataSelectedAttr
        ? {
              inputFieldElement,
              inputWrapperElement,
              popupElement,
              listElement,
          }
        : false;
};

const Dropdown = (element, props) => {
    const [success, mergedProps, validElements] = validateInitialization({
        componentName,
        element,
        defaultProps,
        props,
        views,
        schema,
        validateHTML,
    });

    if (!success) return;

    /**
     * Build notice element to hold content inside dropdown list
     * @returns {HTMLElement} - Notice element
     */
    const buildNotice = () => {
        const noticeNode = createElement("div", {
            class: componentClasses.notice,
            "aria-hidden": "true",
        });

        noticeNode.textContent = mergedProps.noOptionsLabel;
        popupElement.appendChild(noticeNode);

        return noticeNode;
    };

    /**
     * Build and copy name to a hidden input to store dropdown value
     * NOTE: Dropdown input on the HTML is used to control user input/search (Mainly in multiple selection)
     * in order to control what is sent through GET/POST methods
     * we will remove any name that the user inserted in the input and copy it to a hidden where we add values to it
     * @returns {HTMLElement} - Hidden input element
     */
    const buildInputFieldHidden = () => {
        const inputFieldHiddenNode = createElement("input", {
            class: componentClasses.inputFieldHidden,
            type: "hidden",
            name: inputFieldElement.name,
        });

        inputFieldElement.removeAttribute("name");
        inputWrapperElement.appendChild(inputFieldHiddenNode);

        return inputFieldHiddenNode;
    };

    /**
     * Build tag element used in multiple selection
     * @returns {HTMLElement} - Build tag to use in multiple selection
     */
    const buildTag = () => {
        const tagNode = createElement("div", {
            class: `dds__tag dds__tag--gray ${componentClasses.tag} ${
                element.classList.contains(componentClasses.sizes.sm) ? "dds__tag--sm" : ""
            }`,
        });

        renderState(views.tag.base, tagNode);

        const tagTextNodeId = `dropdown-tag-${randomNumGenerator()}`;
        const tagTextNode = createElement("button", {
            tabindex: "-1",
            id: tagTextNodeId,
        });

        inputFieldElement.setAttribute("aria-describedby", tagTextNodeId);

        tagNode.appendChild(tagTextNode);

        const tagDismissNode = createElement("button");
        tagDismissNode.setAttribute("aria-label", mergedProps.srClearLabel);
        tagDismissNode.setAttribute("aria-describedby", tagTextNodeId);
        tagDismissNode.innerHTML = icons.close;
        tagNode.appendChild(tagDismissNode);

        inputWrapperElement.insertBefore(tagNode, inputFieldElement);

        return {
            tagElement: tagNode,
            tagTextElement: tagTextNode,
            tagDismissElement: tagDismissNode,
        };
    };

    /**
     * Build chevron element of the dropdown the svg is wrapped on a button in order to have cursor and also
     * be recognized as a target on blur, since svgs are not considered targets
     * @returns {HTMLElement} - Chevron element
     */
    const buildChevron = () => {
        const chevronButtonNode = createElement("button", {
            class: componentClasses.chevron,
            tabindex: "-1",
            "aria-hidden": "true",
            type: "button", // Button need to have a specific type otherwise inside of forms it will submit
        });

        const chevronNode = new DOMParser().parseFromString(chevronDown(), "text/html").body
            .firstChild;

        chevronButtonNode.appendChild(chevronNode);
        inputWrapperElement.appendChild(chevronButtonNode);

        return chevronButtonNode;
    };

    /**
     * In multiple selection dropdown, after the user select a value a tag is created
     * but the field stays empty showing the placeholder if there is one we are saving the placeholder to remove and add as needed
     */
    const getPlaceholderText = () => {
        return inputFieldElement.getAttribute("placeholder");
    };

    /**
     * Toggle the placeholder in multiple selection
     */
    const togglePlaceholderText = () => {
        if (multipleSelectionValue.length > 0) inputFieldElement.removeAttribute("placeholder");
        else if (placeholderText) inputFieldElement.setAttribute("placeholder", placeholderText);
    };

    /**
     * Set values from multiple selection into the tag after any update
     * NOTE: aria-label is set on tagTextElement so tag dismiss button will point to it through aria-labelledby
     */
    const setTagContent = () => {
        const countSelectedLabel = `${multipleSelectionValue.length} ${mergedProps.selectedLabel}`;
        let selectedValuesLabel = "";

        multipleSelectionValue.forEach((item, index) => {
            const valueEnding = index === multipleSelectionValue.length - 1 ? "" : ", ";
            selectedValuesLabel += `${item.value}${valueEnding}`;
        });

        tagTextElement.textContent = countSelectedLabel;
        tagTextElement.title = selectedValuesLabel;
        tagTextElement.setAttribute("aria-label", `${countSelectedLabel}: ${selectedValuesLabel}`);
    };

    /**
     * Toggle tag visibility and calculate the current width of the tag adding paddings to the field after any updates
     */
    const toggleTagVisibility = () => {
        if (multipleSelectionValue.length === 0) {
            renderState(views.tag.base, tagElement);
            renderState({ classList: [`!${tagDynamicClassMemory}`] }, inputFieldElement);
        } else {
            renderState(views.tag.visible, tagElement);
            const { width } = tagElement.getBoundingClientRect();
            tagDynamicClass = `${prefix}dynamic-dropdown-tag-w${parseInt(width)}`;

            if (
                createCSSClass(`.${tagDynamicClass}`, {
                    "padding-left": `calc(${parseInt(width)}px + 1rem + 0.25rem) !important`, // 1rem lg padding, 0.25rem lg tag margin
                })
            ) {
                renderState(
                    {
                        classList: [
                            tagDynamicClass,
                            tagDynamicClassMemory !== tagDynamicClass &&
                                `!${tagDynamicClassMemory}`,
                        ],
                    },
                    inputFieldElement
                );
            }

            // Save last used class
            tagDynamicClassMemory = tagDynamicClass;
        }
    };

    /**
     * Add check icon svg to the options when in single selection
     * @param {Object} item - Object in the format defined in updateItemsList function
     */
    const setItemCheckIcon = (item) => {
        !item.element.querySelector(`.${componentClasses.itemSelected}`) &&
            item.element.insertAdjacentHTML(
                "beforeend",
                check({ class: componentClasses.itemSelected })
            );
    };

    /**
     * Set class to the outer element to control the css of the options
     */
    const setSelectionStyle = () => {
        isMultiple() && renderState(views.base.isMultiple, element);
    };

    /**
     * Check for multiple/single selection
     * @returns {Boolean}
     */
    const isMultiple = () => {
        return mergedProps.selection === "multiple";
    };

    /**
     * Check if item is focusable (Not disable or hidden by the filtering)
     * @param {Object} item - Object in the format defined in updateItemsList function
     * @returns {Boolean}
     */
    const isItemFocusable = (item) => {
        return (
            item.element.getAttribute("aria-disabled") !== "true" &&
            item.element.getAttribute("aria-hidden") !== "true"
        );
    };

    /**
     * Check if item is disabled by the aria-attribute
     * @param {Object} item - Object in the format defined in updateItemsList function
     * @returns {Boolean}
     */
    const isItemDisabled = (item) => {
        return item.element.getAttribute("aria-disabled") === "true";
    };

    /**
     * Check if item is selected by the data-selected attribute
     * @param {*} item - Object in the format defined in updateItemsList function
     * @returns {Boolean}
     */
    const isItemSelected = (item) => {
        return item.element.getAttribute("data-selected") === "true";
    };

    /**
     * Get the value of an option. If data-value is provided, it will return it, otherwise the text content of the label.
     * @param {*} item - Object in the format defined in updateItemsList function
     * @returns {String} - Value of the option
     */
    const getItemValue = (item) => {
        return item ? item.dataValue || item.value : "";
    };

    /**
     * Center the focus of the input field element.
     */
    const focusInputField = () => {
        inputFieldElement.focus && inputFieldElement.focus();
    };

    /**
     * Set inner values on the field and hidden field
     * Dropdown has a inner field that
     * NOTE: Dropdown store inner values on singleSelectionValue and multipleSelectionValue
     */
    const setValueOnField = () => {
        if (isMultiple()) {
            inputFieldHiddenElement.value = JSON.stringify(
                multipleSelectionValue.map((selectedItem) => {
                    return getItemValue(selectedItem);
                })
            );
        } else {
            inputFieldElement.value = singleSelectionValue ? singleSelectionValue.value : "";
            inputFieldHiddenElement.value = singleSelectionValue
                ? getItemValue(singleSelectionValue)
                : "";
        }
    };

    /**
     * Set inner values
     * NOTE: Dropdown store inner values on singleSelectionValue and multipleSelectionValue
     */
    const setInnerValues = (item) => {
        if (isMultiple()) {
            if (!multipleSelectionValue.includes(item)) multipleSelectionValue.push(item);
        } else {
            singleSelectionValue = item;
        }
    };

    /**
     * Unset inner values
     * NOTE: Dropdown store inner values on singleSelectionValue and multipleSelectionValue
     */
    const unsetInnerValues = (item) => {
        if (isMultiple()) {
            if (multipleSelectionValue.includes(item))
                multipleSelectionValue = multipleSelectionValue.filter((selectedValue) => {
                    return selectedValue !== item;
                });
        } else {
            singleSelectionValue = "";
        }
    };

    /**
     * Clear inner values
     * NOTE: Dropdown store inner values on singleSelectionValue and multipleSelectionValue
     */
    const clearInnerValues = () => {
        if (isMultiple()) {
            multipleSelectionValue = [];
        } else {
            singleSelectionValue = "";
        }
    };

    /**
     * Set data-selected, aria-checked and aria-selected on options
     * NOTE: Accessibility require that multiple selecion use aria-checked instead of aria-selected to readers
     * @param {Object} item - Object in the format defined in updateItemsList function
     * @param {Boolean} selected - Select or unselect the option
     */
    const setItemSelectedAttr = (item, selected) => {
        if (!isMultiple()) {
            renderState(selected ? views.item.selected : views.item.notSelected, item.element);
            return;
        }
        if (selected === "mixed") {
            renderState(views.item.mixed, item.element);
        } else {
            renderState(selected ? views.item.checked : views.item.notchecked, item.element);
        }
    };

    /**
     * Deselect all options data-selected, aria-checked and aria-selected on options
     */
    const deselectAllItemsAttr = () => {
        itemsList.forEach((item) => {
            return setItemSelectedAttr(item, false);
        });
    };

    /**
     * Set data-index to the element to make it easier to find it
     * NOTE: This is done so we can set event listener on each item without sendind the item info creating an inline arrow function to the listener
     * this way we have how to unset the listener
     * @param {Object} item - Object in the format defined in updateItemsList function
     * @param {Number} index - Index of item on itemsList array
     */
    const setItemIndex = (item, index) => {
        item.element.setAttribute("data-index", index);
    };

    /**
     * Set aria properties to readers understand correctly the size of the list
     * @param {Object} item - Object in the format defined in updateItemsList function
     * @param {*} count - Total of options
     */
    const setItemPosInSet = (item, count) => {
        item.element.setAttribute("aria-posinset", item.index + 1);
        item.element.setAttribute("aria-setsize", count);
    };

    /**
     * Set popup element scroll to top
     */
    const setPopupScrollTop = () => {
        if (popupElement) popupElement.scrollTop = 0;
    };

    /**
     * Set the inner variable focusedIndex and focus the item.
     * NOTE: The scroll of the popup works focusing the item, browsers by default will scroll to keep that focused item on screen.
     * When using groups, the group stay above the first item, so if the first item is focused the popup will scroll top to be able to see the group.
     * @param {Object} item - Object in the format defined in updateItemsList function
     */
    const focusItem = (item) => {
        // Remove and add tab index before setting focusedIndex
        focusedIndex = item.index;
        item.element && item.element.focus();
        // Adjust container scrollTop when using groups
        if (focusedIndex === 0) setPopupScrollTop();
    };

    /**
     * Set focus to the first item available on the list
     * @param {Array} itemsListFromIndex - Array in the correct order built by backwardListFromIndex and forwardListFromIndex functions
     */
    const setFocusFirstAvailableItemOnList = (itemsListFromIndex = []) => {
        while (itemsListFromIndex.length) {
            const currentItem = itemsListFromIndex.shift();
            if (currentItem && isItemFocusable(currentItem)) {
                focusItem(currentItem);
                break;
            }
        }
    };

    /**
     * Whenever clicking and item prevent <button> elements from being clicked when disabled.
     * @param {MouseEvent} e - Html click event
     */
    const handleItemClick = (e) => {
        e.preventDefault();
        const itemIndex = parseInt(e.target.dataset.index);

        if (itemIndex === null || itemIndex === undefined) {
            console.warn("Option data-index is undefined");
            return;
        }

        const item = itemsList[itemIndex];
        if (isItemDisabled(item)) return;

        if (isMultiple()) {
            isItemSelected(item) ? deselectOption(item) : selectOption(item);
            // Multiple don't close on select so focus item
            // Single will close the popup and focus the field
            focusItem(item);
        } else {
            selectOption(item);
            visibilityStore.dispatch(actions.close());
        }
    };

    /**
     * Set option event listener
     * @param {Object} item - Object in the format defined in updateItemsList function
     */
    const setItemEventListeners = (item) => {
        item.element.addEventListener("click", handleItemClick);
    };

    /**
     * Remove option event listener
     * @param {Object} item - Object in the format defined in updateItemsList function
     */
    const removeItemEventListeners = (item) => {
        item.element.removeEventListener("click", handleItemClick);
    };

    /**
     * Set user is pressing class modifier to understand when users are using keyboard keys (arrows, home, end)
     * NOTE: Similar to user is tabbing implementation add window click listener to understand when user stop using keyboard
     */
    const setUserIsPressing = () => {
        isPressing = true;
        renderState(views.base.isPressing, element);
        window.addEventListener("click", unsetUserIsPressing);
    };

    /**
     * Unset user is pressing class and remove listeners add by setUserIsPressing
     */
    const unsetUserIsPressing = (e) => {
        // Could use e.detail, but using screen readers the first time the button is clicked with enter/space the detail comes 1
        // If a button is used with enter or space keys a window click is triggered
        // Only disable the is pressing class if the user has clicked and not enter/space keys
        if (e.offsetX !== 0 && e.offsetY !== 0) {
            renderState(views.base.isNotPressing, element);
            window.removeEventListener("click", unsetUserIsPressing);
            isPressing = false;
        }
    };

    /**
     * Filter itemsList and groupsList changing the state of the groups and options based on the term
     * @param {String} term - Term that is being typed on input
     */
    const filterItemsList = (term) => {
        let results = 0;
        let filteredCount = 0;
        const filteredItems = [];
        const hiddenGroups = [...groupsList];
        const isFiltering = Boolean(term);

        // Hide 'select-all' option and its separator while fitler
        if (isMultiple() && mergedProps.selectAll) {
            if (isFiltering) {
                renderState(views.selectAll.hidden, selectAllItem.element);
                renderState(views.selectAll.hidden, selectAllItem.separator);
            } else {
                renderState(views.selectAll.base, selectAllItem.element);
                renderState(views.selectAll.base, selectAllItem.separator);
            }
        }

        itemsList.forEach((item) => {
            // Do not add the Select All to results
            if (mergedProps.selectAll && item.selectAll) return;

            const itemMatchTerm = item.value.toLowerCase().includes(term.toLowerCase());
            const groupMatchTerm =
                item.groupTitle && item.groupTitle.toLowerCase().includes(term.toLowerCase());

            if (itemMatchTerm || groupMatchTerm) {
                results++;
                filteredItems.push(item);
                if (hiddenGroups.indexOf(item.groupElement) >= 0)
                    hiddenGroups.splice(hiddenGroups.indexOf(item.groupElement), 1);
                renderState(views.item.base, item.element);
            } else {
                renderState(views.item.hidden, item.element);
            }
        });

        filteredItems.forEach((item) => {
            filteredCount++;
            item.element.setAttribute("aria-posinset", filteredCount);
            item.element.setAttribute("aria-setsize", results);
        });

        groupsList.forEach((groupElement) => {
            if (hiddenGroups.includes(groupElement)) {
                renderState(views.item.hidden, groupElement);
            } else {
                renderState(views.item.group, groupElement);
            }
        });

        if (results > 0) {
            renderState(views.notice.base, noticeElement);
            renderState(views.list.base, listElement);
        } else {
            renderState(views.notice.visible, noticeElement);
            renderState(views.list.empty, listElement);
        }
    };

    /**
     * Check if the target element is outside of the dropdown component
     * @param {HTMLElementt} target - Target that is being clicked or blur to
     * @returns {Boolean}
     */
    const isTargetOnOuside = (target) => {
        if (isIE && !target) target = document.activeElement;
        return (
            !popupElement.contains(target) &&
            target !== inputFieldElement &&
            target !== chevronElement
        );
    };

    /**
     * Close the popup and clear the field and values if the target element is outside of dropdown component
     * @param {HTMLElement} target - Target that is being clicked or blur to
     */
    const closeOnTargetOutside = (target) => {
        if (!isTargetOnOuside(target)) return;

        if (isMultiple()) {
            // Blur on multiple selection just clear the value (filter)
            inputFieldElement.value = "";
        } else {
            // Blur on single selection if the field is empty remove values else keep values
            if (!inputFieldElement.value) {
                clearSelection();
            } else {
                setValueOnField();
            }
        }

        visibilityStore.dispatch(actions.close());
        filterItemsList(""); // Resetfilter
    };

    /**
     * Handle chevron click to toggle the popup
     * @param {MouseEvent} e - Event object
     */
    const handleChevronClick = () => {
        if (!visibilityStore.getState()) visibilityStore.dispatch(actions.open());
        else visibilityStore.dispatch(actions.close());

        focusInputField();
    };

    /**
     * Handle tag keydown to open the popup
     * @param {KeyboardEvent} e - Event object
     */
    const handleTagElementsKeydown = (e) => {
        switch (e.keyCode) {
            // Arrow down
            case 40:
                e.preventDefault(); // Prevent scrolling even on input
                focusInputField();
                break;
        }
    };

    /**
     * Handle tag click to prevent form submits
     * @param {MouseEvent} e - Event object
     */
    const handleTagTextClick = (e) => {
        e.preventDefault();
    };

    /**
     * Handle tag dismiss click to prevent form submits and clear the selection
     * @param {MouseEvent} e - Event object
     */
    const handleTagDismissClick = (e) => {
        e.preventDefault(); // Prevent forms from submit
        clearSelection();
        focusInputField();
    };

    /**
     * Handle input keydown to toggle popup and focus the list if needed
     * @param {KeyboardEvent} e - Event object
     */
    const handleInputFieldKeydown = (e) => {
        const currentVisibility = visibilityStore.getState();

        switch (e.keyCode) {
            // Enter
            case 13:
                // For some reason, hitting enter on the field was triggering always an item click on the first item of list
                e.preventDefault();
                !currentVisibility
                    ? visibilityStore.dispatch(actions.open())
                    : visibilityStore.dispatch(actions.close());
                break;
            // Esc
            case 27:
                // If there is no search term clear the selection value on single selects
                if (inputFieldElement.value === singleSelectionValue.value) {
                    clearSelection();
                }
                inputFieldElement.value = "";
                // Clears filtered items
                filterItemsList(inputFieldElement.value);
                setValueOnField();
                currentVisibility && visibilityStore.dispatch(actions.close());

                break;
            // Arrow down
            case 40:
                e.preventDefault(); // Prevent scrolling even on input
                if (!isPressing) setUserIsPressing();
                !currentVisibility
                    ? visibilityStore.dispatch(actions.open())
                    : setFocusFirstAvailableItemOnList(forwardListFromIndex(0, itemsList));
                break;
        }
    };

    /**
     * Handle input change to open and filter the list of options
     * @param {InputEvent} e - Event object
     */
    const handleInputFieldChange = (e) => {
        if (!visibilityStore.getState()) visibilityStore.dispatch(actions.open());
        filterItemsList(e.target.value);
    };

    /**
     * Handle input click to toggle the popup
     */
    const handleInputFieldClick = () => {
        if (!visibilityStore.getState()) visibilityStore.dispatch(actions.open());
        else visibilityStore.dispatch(actions.close());
    };

    /**
     * Handle input blur sendind the related target to check if popup need to be closed
     */
    const handleInputFieldBlur = (e) => {
        closeOnTargetOutside(e.relatedTarget);
    };

    /**
     * Handle popup keydown to close the popup on esc
     * @param {KeyboardEvent} e - Event object
     */
    const handlePopupKeydown = (e) => {
        // Esc
        if (e.keyCode === 27) {
            if (!isPressing) setUserIsPressing();
            visibilityStore.dispatch(actions.close());
            focusInputField();
        }

        // Right Arrow(39) and Left Arrow(39)
        if (e.keyCode === 39 || e.keyCode === 37) {
            if (!isPressing) setUserIsPressing();
            visibilityStore.dispatch(actions.close());
            focusInputField();
        }
    };

    /**
     * Handle popup blur sendind the related target to check if popup need to be closed
     */
    const handlePopupBlur = (e) => {
        closeOnTargetOutside(e.relatedTarget);
    };

    /**
     * Handle list keydown control the focus and option focusing with arrow/home/end
     * @param {KeyboardEvent} e - Event object
     */
    const handleListKeydown = (e) => {
        if (!isPressing && [35, 36, 38, 40].includes(e.keyCode)) setUserIsPressing();

        switch (e.keyCode) {
            // Backspace
            case 8:
                focusInputField();
                break;
            // End
            case 35:
                e.preventDefault(); // Prevent scrolling
                setFocusFirstAvailableItemOnList(
                    backwardListFromIndex(itemsList.length - 1, itemsList)
                );
                break;
            // Home
            case 36:
                e.preventDefault(); // Prevent scrolling
                setFocusFirstAvailableItemOnList(forwardListFromIndex(0, itemsList));
                break;
            // Arrow up
            case 38: {
                e.preventDefault(); // Prevent scrolling
                const prevIndex = focusedIndex === 0 ? itemsList.length - 1 : focusedIndex - 1;
                setFocusFirstAvailableItemOnList(backwardListFromIndex(prevIndex, itemsList));
                break;
            }
            // Arrow down
            case 40: {
                e.preventDefault(); // Prevent scrolling
                const nextIndex = focusedIndex + 1 === itemsList.length ? 0 : focusedIndex + 1;
                setFocusFirstAvailableItemOnList(forwardListFromIndex(nextIndex, itemsList));
                break;
            }
        }
    };

    /**
     * Handle tag and list keypress, when user type any key that generates value, focus the field and start typing
     * NOTE: is deprecated but doesn't seems to have a decent substitute for it, recognize keys that return values
     */
    const handleElementsKeypress = () => {
        focusInputField();
    };

    /**
     * Handle window resizing to close the popup
     * NOTE: Will only trigger in desktop because mobile keyboard trigger resize and we cannot close the popup at this moment
     */
    const handleWindowResize = debounce(() => {
        // Mobile opens the keyboard generating a window resize we are preventing it from close
        if (!isMobile() && visibilityStore.getState()) visibilityStore.dispatch(actions.close());
    }, 100);

    /**
     * Runs whenever the popup visibility turns to true to calculate the position the the popup need to be related to the input
     */
    const updatePopupPosition = () => {
        const { top, left } = getPosition(inputFieldElement, popupElement, "bottom", {
            x: 4,
            y: 4,
        });

        popupDynamicClass = `${prefix}dynamic-dropdown-t${parseInt(top)}-l${parseInt(left)}`;

        if (
            createCSSClass(`.${popupDynamicClass}`, {
                transform: `translate(${parseInt(left)}px, ${parseInt(top)}px) !important`,
            })
        ) {
            renderState(
                {
                    classList: [
                        popupDynamicClass,
                        popupDynamicClassMemory !== popupDynamicClass &&
                            `!${popupDynamicClassMemory}`,
                    ],
                },
                popupElement
            );
        }

        // Save last used class
        popupDynamicClassMemory = popupDynamicClass;
    };

    /**
     * This is the main function of the dropdown it will run on the start of the component and whenever the DOM subtree of the list changed
     * It loop options and create an array of options and groups with a predefined item object,
     * after that, any changes occurring to the component is based on that item structure
     */
    const updateItemsList = () => {
        // Clear list and values
        groupsList = [];
        itemsList = [];
        singleSelectionValue = "";
        multipleSelectionValue = [];

        groupsList = Array.from(listElement.querySelectorAll(`.${componentClasses.groupTitle}`));
        const itemsArray = Array.from(listElement.querySelectorAll(`.${componentClasses.item}`)); // IE 11 requires array to be able to forEach
        const itemsCount = itemsArray.length;

        itemsArray.forEach((item, index) => {
            const option = item.querySelector(`.${componentClasses.itemOption}`);
            const label = item.querySelector(`.${componentClasses.itemLabel}`);

            const value = label && label.textContent.trim();
            const dataValue =
                option &&
                option.getAttribute("data-value") &&
                option.getAttribute("data-value").trim();
            let groupElement = null;
            let groupTitle = null;

            if (item.parentElement.classList.contains(componentClasses.group)) {
                const groupId = item.parentElement.getAttribute("aria-labelledby");
                groupElement = listElement.querySelector(`#${groupId}`);
                groupTitle = groupElement && groupElement.textContent;
            }

            const itemInfo = {
                value,
                dataValue,
                element: option,
                index,
                groupTitle,
                groupElement,
                selectAll: false,
            };

            setItemIndex(itemInfo, index);
            setItemPosInSet(itemInfo, itemsCount);

            if (item.classList.contains(componentClasses.selectAll)) {
                itemInfo.selectAll = true;
                itemsList.push(itemInfo);
            } else {
                // Because this occurs when childrens are updated, make sure to always remove before adding
                removeItemEventListeners(itemInfo);
                setItemEventListeners(itemInfo);
                // The decision o making the data-selected instead of directly aria-selected,
                // is regards the maintanability, if the aria attribute is different for single/multiple
                // the data-selected will be always the same html markup
                setItemSelectedAttr(itemInfo, isItemSelected(itemInfo));

                if (!isMultiple()) setItemCheckIcon(itemInfo);

                // Set inner values
                if (isItemSelected(itemInfo)) setInnerValues(itemInfo);

                itemsList.push(itemInfo);
            }
        });

        setValueOnField();

        if (isMultiple()) {
            mergedProps.selectAll && setSelectAllState();
            setTagContent();
            toggleTagVisibility();
        }

        // Build notice if no items are available
        if (itemsCount > 0) {
            renderState(views.notice.base, noticeElement);
            renderState(views.list.base, listElement);
        } else {
            renderState(views.notice.visible, noticeElement);
            renderState(views.list.empty, listElement);
        }
    };

    /**
     * Wrapper the updateItemsList into a debounce function
     * Need to be on component scope to keep the reference
     */
    const updateItemsListDebouncing = debounce(updateItemsList, 200);

    /**
     * Callback for mutation observer
     * @param {*} mutationsList - MutationObserver mutationList
     */
    const itemsListObserverCallback = (mutationsList) => {
        const hasChildChange = mutationsList.some((mutation) => {
            return mutation.type === "childList";
        });
        if (hasChildChange) updateItemsListDebouncing();
    };

    /**
     * Callback for visibility store
     * @param {Boolean} visible
     */
    const handleVisibleStateChange = (visible) => {
        if (visible) {
            // The popup element was being controlled by aria-hidden property
            // For some reason, doing that was affecting the input with screen readers
            // Once the popup menu was appearing with aria-hidden="false" the screen reader was
            // no reading arrow left and right letters anymore, that is why views.popup.visible and hidden
            // were swapped to classes
            renderState(views.popup.positioning, popupElement);
            updatePopupPosition();
            renderState(views.popup.visible, popupElement);
            setPopupScrollTop();
            renderState(views.base.isOpen, element);
            inputWrapperElement.setAttribute("aria-expanded", "true");
            inputFieldElement.setAttribute("aria-expanded", "true");
        } else {
            renderState(views.popup.hidden, popupElement);
            renderState(views.base.isClose, element);
            inputWrapperElement.setAttribute("aria-expanded", "false");
            inputFieldElement.setAttribute("aria-expanded", "false");
        }
    };

    /**
     * Based on the parameter type return the related inner option based on itemsList
     * @param {*} item - String for api call and Object for inner calls
     * @returns {Object} - Object in the format defined in updateItemsList function
     */
    const getInnerItem = (item) => {
        if (!item) return console.warn("Parameter is invalid");

        let innerItem;
        if (typeof item === "object") {
            innerItem = item;
        } else if (typeof item === "string") {
            const itemIndex = itemsList.findIndex((listItem) => {
                return getItemValue(listItem) === item;
            });
            if (itemIndex < 0) return console.warn(`The value "${item}" provided is not an item`);
            innerItem = itemsList[itemIndex];
        } else {
            return console.warn("Parameter item format is invalid");
        }

        return innerItem;
    };

    /**
     * Api method to get the value of the dropdown
     * @returns {String|Array} - value
     */
    const getValue = () => {
        return isMultiple()
            ? multipleSelectionValue.map((item) => {
                  return getItemValue(item);
              })
            : getItemValue(singleSelectionValue);
    };

    /**
     * Alias for getValue
     */
    const getSelection = () => {
        return getValue();
    };

    /**
     * Select option
     * NOTE: String for api call and Object for inner calls
     * @returns {String|Object} - String or Object in the format defined in updateItemsList function
     */
    const selectOption = (item) => {
        const innerItem = getInnerItem(item);
        if (!innerItem) return;

        if (!isMultiple()) deselectAllItemsAttr();

        setItemSelectedAttr(innerItem, true);
        setInnerValues(innerItem);
        setValueOnField();

        if (isMultiple()) {
            setTagContent();
            toggleTagVisibility();
            togglePlaceholderText();

            if (mergedProps.selectAll) setSelectAllState();
        }

        dispatchEvent(eventMap.selectionChange, element, { value: getValue() });
    };

    /**
     * Deselect option
     * NOTE: String for api call and Object for inner calls
     * @returns {String|Object} - String or Object in the format defined in updateItemsList function
     */
    const deselectOption = (item) => {
        const innerItem = getInnerItem(item);
        if (!innerItem) return;

        if (isMultiple()) {
            setItemSelectedAttr(innerItem, false);
            unsetInnerValues(innerItem);
            setValueOnField();
            setTagContent();
            toggleTagVisibility();
            togglePlaceholderText();

            if (mergedProps.selectAll) setSelectAllState();

            dispatchEvent(eventMap.selectionChange, element, { value: getValue() });
        } else {
            clearSelection();
        }
    };

    /**
     * Clear selection
     */
    const clearSelection = () => {
        if (isMultiple()) {
            deselectAllItemsAttr();
            clearInnerValues();
            setValueOnField();
            setTagContent();
            toggleTagVisibility();
            togglePlaceholderText();
        } else {
            deselectAllItemsAttr();
            clearInnerValues();
            setValueOnField();
        }

        dispatchEvent(eventMap.selectionChange, element, { value: getValue() });
    };

    /**
     * Build 'Select All' HTML and prepend it to the List element
     * @returns {Object} - Object with the 'Select All' Item element
     */
    const buildSelectAll = () => {
        listElement.insertAdjacentHTML(
            "afterbegin",
            `${DropdownItemTemplate({
                selectAll: true,
                selectAllLabel: mergedProps.selectAllLabel,
            })}
            ${DropdownSeparatorTemplate()}`
        );

        return {
            element: listElement.querySelector(
                `.${componentClasses.selectAll} .${componentClasses.itemOption}`
            ),
            separator: listElement.querySelector(`.${componentClasses.listItemSeparator}`),
        };
    };

    /**
     * Check the state of the list elements and render the check/unchecked/mixed on Select All
     */
    const setSelectAllState = () => {
        // Variable of number to account for select all options and number of disabled items to remove from list
        const availableItems = itemsList.reduce((acc, item) => {
            if (isItemDisabled(item) || item.selectAll) {
                return acc - 1;
            }
            return acc;
        }, itemsList.length);

        if (multipleSelectionValue.length > 0 && multipleSelectionValue.length < availableItems) {
            setItemSelectedAttr(selectAllItem, "mixed");
        } else if (multipleSelectionValue.length === 0) {
            setItemSelectedAttr(selectAllItem, false);
        } else if (multipleSelectionValue.length === availableItems) {
            setItemSelectedAttr(selectAllItem, true);
        }
    };

    const handleSelectAllClick = () => {
        if (
            selectAllItem.element.getAttribute("data-selected") === "mixed" ||
            !isItemSelected(selectAllItem)
        ) {
            selectAllOptions();
        } else {
            clearSelection();
        }
        focusItem(selectAllItem);
    };

    const selectAllOptions = () => {
        if (!isMultiple()) return;
        setItemSelectedAttr(selectAllItem, true);
        itemsList.forEach((item) => {
            // Dont Select if Item is disabled
            if (isItemDisabled(item)) return;

            if (item.element !== selectAllItem.element) {
                setItemSelectedAttr(item, true);
                setInnerValues(item);
            }
        });

        setValueOnField();

        setTagContent();
        toggleTagVisibility();
        togglePlaceholderText();
        setSelectAllState();

        dispatchEvent(eventMap.selectionChange, element, { value: getValue() });
    };

    const dispose = () => {
        element[componentName] = null;
        element = null;
        api = null;

        // Disconnect observer
        itemsListObserver.disconnect();

        // Remove items event listeners
        itemsList.forEach((item) => {
            item.element.removeEventListener("click", handleItemClick);
        });

        // Remove tag event listeners
        if (isMultiple()) {
            tagElement.removeEventListener("keypress", handleElementsKeypress);
            tagTextElement.removeEventListener("keydown", handleTagElementsKeydown);
            tagDismissElement.removeEventListener("keydown", handleTagElementsKeydown);
            tagTextElement.removeEventListener("click", handleTagTextClick);
            tagDismissElement.removeEventListener("click", handleTagDismissClick);
        }

        // Removing standard event listeners
        inputFieldElement.removeEventListener("keydown", handleInputFieldKeydown);
        inputFieldElement.removeEventListener("input", handleInputFieldChange);
        inputFieldElement.removeEventListener("blur", handleInputFieldBlur);
        inputFieldElement.removeEventListener("click", handleInputFieldClick);
        popupElement.removeEventListener("keydown", handlePopupKeydown);
        popupElement.removeEventListener("blur", handlePopupBlur, true);
        listElement.removeEventListener("keydown", handleListKeydown);
        listElement.removeEventListener("keypress", handleElementsKeypress);
        chevronElement.removeEventListener("click", handleChevronClick);
        window.removeEventListener("resize", handleWindowResize, true);
    };

    // Get elements from validateInitialization
    const { inputFieldElement, inputWrapperElement, popupElement, listElement } = validElements;

    let itemsList = [];
    let groupsList = [];
    let singleSelectionValue = "";
    let multipleSelectionValue = [];
    let focusedIndex = 0;
    let isPressing = false;
    let popupDynamicClass = null;
    let popupDynamicClassMemory = null;
    let tagDynamicClass = null;
    let tagDynamicClassMemory = null;
    let tagElement = null;
    let tagTextElement = null;
    let tagDismissElement = null;
    let selectAllItem = null;

    // Build tag if is multiple
    if (isMultiple()) {
        const tag = buildTag();
        tagElement = tag.tagElement;
        tagTextElement = tag.tagTextElement;
        tagDismissElement = tag.tagDismissElement;
        tagElement.addEventListener("keypress", handleElementsKeypress);
        tagTextElement.addEventListener("keydown", handleTagElementsKeydown);
        tagDismissElement.addEventListener("keydown", handleTagElementsKeydown);
        tagTextElement.addEventListener("click", handleTagTextClick);
        tagDismissElement.addEventListener("click", handleTagDismissClick);

        if (mergedProps.selectAll) {
            selectAllItem = buildSelectAll();
            selectAllItem.element.addEventListener("click", handleSelectAllClick);
        }
    }

    // Build elements and return the element
    const noticeElement = buildNotice();
    const inputFieldHiddenElement = buildInputFieldHidden();
    const chevronElement = buildChevron();
    const placeholderText = getPlaceholderText();

    updateItemsList();
    setSelectionStyle();

    // Start observing the listElement node for configured mutations
    // Mutation observer starts after first updateItemsList() because it changes the children nodes adding svgs
    const listObserverConfig = { childList: true };
    const itemsListObserver = new MutationObserver(itemsListObserverCallback);
    itemsListObserver.observe(listElement, listObserverConfig);

    const visibilityStore = createStore(reducers.boolReducer, false);
    visibilityStore.subscribe(handleVisibleStateChange);

    inputFieldElement.addEventListener("keydown", handleInputFieldKeydown);
    inputFieldElement.addEventListener("input", handleInputFieldChange);
    inputFieldElement.addEventListener("blur", handleInputFieldBlur);
    inputFieldElement.addEventListener("click", handleInputFieldClick);
    popupElement.addEventListener("keydown", handlePopupKeydown);
    popupElement.addEventListener("blur", handlePopupBlur, true);
    listElement.addEventListener("keydown", handleListKeydown);
    listElement.addEventListener("keypress", handleElementsKeypress);
    chevronElement.addEventListener("click", handleChevronClick);
    window.addEventListener("resize", handleWindowResize);

    let api = {
        element,
        dispose,
        clearSelection,
        selectAllOptions,
        getValue,
        getSelection,
        selectOption,
        deselectOption,
    };

    element[componentName] = api;

    return api;
};

export default Dropdown;
